import { useEffect, useState } from "react";
import { Tv, Send, Loader2, ExternalLink, Flame } from "lucide-react";
import { toast } from "sonner";
import { latestTrending, sendTrending } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";

const Heat = ({ n }) => (
  <span className="inline-flex items-center gap-0.5" title={`Controversy ${n}/5`} data-testid="trending-video-heat">
    {[1, 2, 3, 4, 5].map((i) => <Flame key={i} className={`w-3 h-3 ${i <= n ? "text-[#c2410c]" : "text-slate-200"}`} />)}
  </span>
);

const VideoRow = ({ v }) => (
  <li data-testid="trending-video" className="flex gap-3 py-3 border-t border-slate-100">
    <a href={v.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
      <img src={v.thumbnail} alt="" loading="lazy" className="w-28 aspect-video object-cover border border-slate-200" />
    </a>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-2 text-[10px] uppercase tracking-wider text-slate-400">
        <span className="text-[var(--accent)]">{v.lane}</span>
        {v.topic && <span>· {v.topic}</span>}
        <Heat n={v.controversy} />
      </div>
      <a href={v.url} target="_blank" rel="noopener noreferrer" data-testid="trending-video-link" className="block text-sm font-medium text-[#0A192F] hover:text-[var(--accent)] leading-snug mt-0.5">
        {v.title} <ExternalLink className="inline w-3 h-3 align-baseline" />
      </a>
      <div className="text-xs text-slate-500">{v.channel}{v.published ? ` · ${v.published}` : ""}</div>
      <p className="text-xs text-slate-600 mt-1">{v.why_trending}</p>
      <p className="text-xs text-[#0A192F] mt-1"><span className="font-medium">ATFQ angle:</span> {v.atfq_angle}</p>
    </div>
  </li>
);

export const TrendingNews = ({ schedule }) => {
  const [doc, setDoc] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    latestTrending().then(setDoc).catch(() => setMissing(true));
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      await sendTrending();
      toast.success("Scanning the news — the brief lands in your inbox in a couple of minutes.");
    } catch (err) {
      toast.error(extractApiError(err, "Could not start the scan."));
    } finally {
      setBusy(false);
    }
  };

  if (!doc && !missing) return null;
  const next = schedule?.next_trending
    ? new Date(schedule.next_trending).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit", timeZone: schedule.timezone || undefined })
    : null;

  return (
    <section data-testid="scout-trending-news" className="border-t border-slate-200 pt-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="text-xs tracking-tight text-[var(--accent)] flex items-center gap-2">
          <Tv className="w-3.5 h-3.5" /> News Night Brief · weekdays{next ? ` · next ${next}` : ""}
        </div>
        <button type="button" data-testid="scout-trending-send" onClick={send} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-[var(--accent)] hover:text-[#0A192F] disabled:opacity-50">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {busy ? "Starting…" : "Scan & email now"}
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-2">3 trending political news videos a day, picked for the ethos. Review them in the email.</p>
      {missing || !doc?.videos?.length ? (
        <p data-testid="scout-trending-news-empty" className="text-sm text-slate-500">No brief yet — the first arrives the next weekday morning, or hit &ldquo;Scan &amp; email now&rdquo;.</p>
      ) : (
        <>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">{doc.day} · {doc.summary}</div>
          <ul>{doc.videos.map((v) => <VideoRow key={v.video_id} v={v} />)}</ul>
        </>
      )}
    </section>
  );
};

export default TrendingNews;
